import { Check } from "lucide-react";
import { Eyebrow, Reveal, scrollToId, openCalendly } from "./utils";

const packages = [
  {
    name: "Cost Effective",
    price: "$999",
    tagline: "Stop losing easy revenue.",
    popular: false,
    features: [
      "CRM setup, cleanup, and customer organization",
      "Lead pipeline and customer tracking",
      "Missed-call and web-form follow-up automation",
      "Basic quote and estimate follow-up reminders",
      "Review request automation",
      "Past-customer reactivation setup",
      "Basic customer segmentation",
      "Monthly performance report",
      "Ongoing automation maintenance and support",
      "Access to the configured Apex Flow dashboard",
    ],
  },
  {
    name: "Advanced Growth",
    price: "$1,499",
    tagline: "Build a more predictable growth system.",
    popular: true,
    features: [
      "Everything in Cost Effective",
      "Advanced SMS and email follow-up sequences",
      "Multi-step lead nurture campaigns",
      "Customer reactivation campaigns",
      "Deeper customer segmentation",
      "Campaign tracking and conversion reporting",
      "Website and booking-flow conversion recommendations",
      "Google Business Profile and review-growth recommendations",
      "Monthly campaign optimization",
      "Growth Campaign Activation based on the highest-value opportunity",
      "One monthly 30-minute Revenue Strategy Review",
    ],
  },
  {
    name: "Apex Ultimate",
    price: "$1,999",
    tagline: "A hands-on revenue growth partner.",
    popular: false,
    features: [
      "Everything in Advanced Growth",
      "Priority CRM, automation, and campaign optimization",
      "Advanced sales-pipeline and quote-follow-up optimization",
      "Referral, retention, upsell, and recurring-revenue strategy",
      "Custom seasonal, promotional, reactivation, referral, and upsell campaigns",
      "AI-supported campaign assets within agreed scope",
      "Deeper revenue-opportunity tracking",
      "Strategic recommendations for paid ads, direct mail, SEO, and lead generation",
      "Priority support",
      "Two Revenue Strategy Reviews per month",
    ],
  },
];

export default function Packages() {
  return (
    <section data-testid="pricing-packages" id="packages" className="relative overflow-hidden bg-white py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>Ongoing Growth Support</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-brand-black leading-[1.08]">
              Choose the Level of Support That <span className="gold-text">Fits Your Business.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-brand-black/65">
              Every Apex Flow package includes a three-month minimum commitment. Recommendations are based on what the
              business actually needs—not on selling the largest package.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-3 gap-7 lg:gap-6 items-start">
          {packages.map((p, i) => (
            <Reveal key={p.name} delay={i}>
              <div
                data-testid={`package-card-${i}`}
                className={`relative h-full rounded-3xl p-8 transition-all duration-300 ${
                  p.popular
                    ? "bg-brand-black text-white shadow-[0_40px_90px_-30px_rgba(17,17,17,0.5)] lg:-translate-y-4 border border-brand-gold/30"
                    : "bg-white border border-brand-gray-soft shadow-luxe hover:-translate-y-1.5 hover:border-brand-gold/40"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-gold to-brand-gold-deep px-4 py-1.5 font-sans text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-luxe-gold">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-sans text-lg font-semibold tracking-tight ${p.popular ? "text-white" : "text-brand-black"}`}>
                  {p.name}
                </h3>
                <p className={`mt-1 font-sans text-sm ${p.popular ? "text-brand-gold" : "text-brand-gold-deep"}`}>{p.tagline}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className={`font-serif text-5xl leading-none ${p.popular ? "text-white" : "text-brand-black"}`}>{p.price}</span>
                  <span className={`mb-1 font-sans text-sm ${p.popular ? "text-white/55" : "text-brand-black/45"}`}>/month</span>
                </div>

                <button
                  data-testid={`package-book-${i}`}
                  onClick={openCalendly}
                  className={`mt-7 w-full rounded-full px-6 py-3.5 font-sans text-sm font-semibold tracking-wide transition-all duration-300 ${
                    p.popular
                      ? "bg-gradient-to-br from-brand-gold to-brand-gold-deep text-white hover:-translate-y-0.5 shadow-luxe-gold"
                      : "bg-brand-black text-white hover:-translate-y-0.5 hover:shadow-luxe"
                  }`}
                >
                  Book a Free Call
                </button>

                <ul className={`mt-7 space-y-3 border-t pt-7 ${p.popular ? "border-white/10" : "border-brand-gray-soft"}`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" strokeWidth={2} />
                      <span className={`font-sans text-sm leading-snug ${p.popular ? "text-white/75" : "text-brand-black/70"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={1}>
          <p className="mt-10 mx-auto max-w-3xl text-center font-sans text-sm leading-relaxed text-brand-black/55">
            Additional paid ad spend beyond Apex Flow’s internally included Growth Campaign Activation requires client
            approval.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
